import {
  addModulesLocale,
  getLocaleByPriority,
  setLocaleForApp,
} from '@/libs/functions/lang'
import { EventCenter } from '../EventCenter'
import { LangStore } from './LangStore'
import {
  getEventNameForLoadModuleDataFromMaster,
  MICRO_LOCALE_CHANGED,
  MICRO_LOCALE_DATA_REQUEST,
} from '@/libs/constant'
import { getLangPacksVersionAndUrls } from '@/services/lang'
import { getUrlParam } from '@/libs/functions/getValue'
import { getStorage } from '@/libs/functions/onStorage'
import { getLocaleByLocal } from './utils'
import { LangCache } from './LangCache'
import {
  ActiveLocaleResources,
  LangChangeProps,
  LangResponse,
  LocaleMessages,
  MicroLocaleDeliveryMode,
  MicroLocaleDataRequest,
  ModuleInfoMap,
  ModuleResource,
  ModuleResources,
} from './types'

LangStore.initialize()

export class LangController {
  /** 当前页面实际使用的语种文本，不保存完整三语种语言包。 */
  private activeResources = new Map<string, LocaleMessages>()
  private moduleRequests = new Map<string, Promise<ModuleResource>>()
  private appRequests = new Map<string, Promise<void>>()
  private modulesInfoMap: ModuleInfoMap = {}
  private readonly langCache = new LangCache()
  private readonly metadataReady: Promise<LangResponse>
  private eventCenter: EventCenter
  public currentLocale = ''
  private isUseLocaleFromTenantSetting = false
  private supportLangs: string[] = LangStore.getSupportLangs()

  constructor(eventCenter: EventCenter, renderLangPosible: boolean = false) {
    const localeInfo = getLocaleByPriority()
    const { locale, isNeedResetLocale } = localeInfo
    this.eventCenter = eventCenter
    window.eventCenter = eventCenter
    this.currentLocale = locale
    this.isUseLocaleFromTenantSetting = isNeedResetLocale
    this.initDefaultLocale(localeInfo)
    this.addEventListeners()

    this.metadataReady = this.getLangVersionAndUrl()
    void this.metadataReady
      .then(() => this.loadSharedModules())
      .catch((error) => this.logLoadError('language metadata', error))
  }

  initDefaultLocale(localeInfo: {
    locale: string
    bossLocale: string
    isNeedResetLocale: boolean
  }) {
    setLocaleForApp({
      locale: localeInfo.locale,
      forceUpdateWindowLang: true,
    })
  }

  getTenantCode() {
    const storageTenantCode = getStorage('tenantCode') || ''
    const tenantCode = getUrlParam('tenantCode') || ''
    if (tenantCode && storageTenantCode !== tenantCode) {
      return tenantCode !== 'undefined' ? tenantCode : ''
    }
    return storageTenantCode && storageTenantCode !== 'undefined'
      ? storageTenantCode
      : ''
  }

  async getLangVersionAndUrl(): Promise<LangResponse> {
    const res = await getLangPacksVersionAndUrls({
      channel: 'h5',
      moduleCodes: LangStore.getAllModules(),
      tenantCode: this.getTenantCode(),
    })

    this.modulesInfoMap = res.lang || {}

    if (res.supportLangs?.length) {
      this.supportLangs = res.supportLangs
      LangStore.setSupportLangs(res.supportLangs)
    }

    if (this.isUseLocaleFromTenantSetting) {
      const defaultLang = this.supportLangs.includes(res.defaultLang)
        ? res.defaultLang
        : this.supportLangs[0]
      this.currentLocale = defaultLang
      setLocaleForApp({ locale: defaultLang })
    }

    return res
  }

  /** 主应用启动时仅向 Umi 注册当前语种。 */
  async loadSharedModules(): Promise<ActiveLocaleResources> {
    await this.metadataReady
    const locale = this.currentLocale
    const modules = LangStore.getSharedModules()
    const resources = await this.loadActiveResources(modules, locale)

    if (locale === this.currentLocale) {
      addModulesLocale(locale as 'zh-CN' | 'en-US' | 'zh-TW', resources)
    }
    return resources
  }

  private addEventListeners() {
    this.watchMicroAppLangChange()
    this.watchMicroAppLangFetch()
  }

  private watchMicroAppLangFetch() {
    const handler = (request: MicroLocaleDataRequest) => {
      if (!request?.microName) return

      const locale = request.locale || this.currentLocale
      const deliveryMode = request.deliveryMode
      void this.requestAppResources(
        request.microName,
        locale,
        deliveryMode,
      ).catch((error) =>
        this.logLoadError(`micro app ${request.microName}`, error),
      )
    }
    this.eventCenter.on(MICRO_LOCALE_DATA_REQUEST, handler)
  }

  private watchMicroAppLangChange() {
    const handler = ({ locale: newLocale }: LangChangeProps) => {
      const { locale: previousLocale } = getLocaleByLocal()
      if (previousLocale === newLocale && this.currentLocale === newLocale)
        return

      this.currentLocale = newLocale
      this.activeResources.clear()
      void this.loadSharedModules().catch((error) =>
        this.logLoadError(`locale ${newLocale}`, error),
      )
    }
    this.eventCenter.on(MICRO_LOCALE_CHANGED, handler)
  }

  private requestAppResources(
    microName: string,
    locale: string,
    deliveryMode?: MicroLocaleDeliveryMode,
  ): Promise<void> {
    const key = `${microName}:${locale}:${deliveryMode || 'all-locales'}`
    const currentRequest = this.appRequests.get(key)
    if (currentRequest) return currentRequest

    const request = this.loadAppResources(
      microName,
      locale,
      deliveryMode,
    ).finally(() => this.appRequests.delete(key))
    this.appRequests.set(key, request)
    return request
  }

  /**
   * goods 优先获得 IndexedDB 引用通知；旧子应用保持全量协议。
   */
  async loadAppResources(
    microName: string,
    requestedLocale = this.currentLocale,
    deliveryMode?: MicroLocaleDeliveryMode,
  ): Promise<void> {
    await this.metadataReady
    // 无本地选择时，微应用可能在租户默认语种返回前请求了临时语言。
    const locale = this.isUseLocaleFromTenantSetting
      ? this.currentLocale
      : requestedLocale
    const modules = LangStore.getAppModules(microName)
    const eventName = getEventNameForLoadModuleDataFromMaster(microName)

    if (deliveryMode === 'idb-ready') {
      await this.ensureModulesCached(modules)
      this.eventCenter.emit(eventName, {
        microName,
        locale,
        deliveryMode,
        modules: modules.map((module) => ({
          module,
          version: this.modulesInfoMap[module].version,
        })),
      })
      return
    }

    if (deliveryMode === 'active-locale-fallback') {
      const resources = await this.loadActiveResources(modules, locale)
      this.eventCenter.emit(eventName, {
        microName,
        locale,
        deliveryMode,
        resources,
      })
      return
    }

    const resources: ModuleResources = {}
    await Promise.all(
      modules.map(async (module) => {
        resources[module] = await this.loadModuleResource(module)
      }),
    )
    this.eventCenter.emit(eventName, {
      microName,
      modules,
      resources,
    })
  }

  async loadModules(modules: string[], ignoreError = false): Promise<void> {
    const results = await Promise.allSettled(
      modules.map((module) =>
        this.loadModuleLocale(module, this.currentLocale),
      ),
    )
    const failed = results.find((result) => result.status === 'rejected')
    if (failed && !ignoreError) {
      throw (failed as PromiseRejectedResult).reason
    }
  }

  getCurrentLocale() {
    return this.currentLocale
  }

  getCachedResources(module: string): LocaleMessages | undefined {
    return this.activeResources.get(
      this.getActiveResourceKey(module, this.currentLocale),
    )
  }

  clearModuleCache(module: string): void {
    const prefix = `${module}:`
    Array.from(this.activeResources.keys()).forEach((key) => {
      if (key.startsWith(prefix)) this.activeResources.delete(key)
    })
  }

  getSupportLangs(): string[] {
    return [...this.supportLangs]
  }

  isLangSupported(lang: string): boolean {
    return this.supportLangs.includes(lang)
  }

  private async loadActiveResources(
    modules: string[],
    locale: string,
  ): Promise<ActiveLocaleResources> {
    const resources: ActiveLocaleResources = {}
    await Promise.all(
      modules.map(async (module) => {
        resources[module] = await this.loadModuleLocale(module, locale)
      }),
    )
    return resources
  }

  /**
   * 在通知 goods 前确保模块已落盘。IDB 不可用时不会抛错，goods 会请求文本回退。
   */
  private async ensureModulesCached(modules: string[]): Promise<void> {
    await Promise.all(
      modules.map(async (module) => {
        const info = this.modulesInfoMap[module]
        if (!info) throw new Error(`[Lang] ${module} Not found`)

        const cached = await this.langCache.get(module)
        if (cached?.version === info.version) return

        const resources = await this.loadModuleResource(module)
        await this.langCache.put({
          module,
          version: info.version,
          resources,
          updatedAt: Date.now(),
        })
      }),
    )
  }

  private async loadModuleLocale(
    module: string,
    locale: string,
  ): Promise<LocaleMessages> {
    const key = this.getActiveResourceKey(module, locale)
    const cached = this.activeResources.get(key)
    if (cached) return cached

    const moduleResource = await this.loadModuleResource(module)
    const messages = this.pickLocale(moduleResource, locale)
    this.clearModuleCache(module)
    this.activeResources.set(key, messages)
    return messages
  }

  /** 从 IndexedDB 取到全量包后只在当前调用栈内投影，不长期保留。 */
  private async loadModuleResource(module: string): Promise<ModuleResource> {
    const info = this.modulesInfoMap[module]
    if (!info) throw new Error(`[Lang] ${module} Not found`)

    const pending = this.moduleRequests.get(module)
    if (pending) return pending

    const request = (async () => {
      const cached = await this.langCache.get(module)
      if (cached?.version === info.version) return cached.resources

      const resources = await this.fetchResources(module)
      void this.langCache.put({
        module,
        version: info.version,
        resources,
        updatedAt: Date.now(),
      })
      return resources
    })()

    this.moduleRequests.set(module, request)
    try {
      return await request
    } finally {
      this.moduleRequests.delete(module)
    }
  }

  private async fetchResources(module: string): Promise<ModuleResource> {
    const response = await fetch(this.modulesInfoMap[module].allLangUrl)
    if (!response.ok) {
      throw new Error(`[Lang] HTTP ${response.status} loading ${module}`)
    }
    return response.json()
  }

  private pickLocale(
    resources: ModuleResource,
    locale: string,
  ): LocaleMessages {
    return Object.entries(resources).reduce<LocaleMessages>(
      (messages, [key, translations]) => {
        const message = translations[locale]
        if (typeof message === 'string') messages[key] = message
        return messages
      },
      {},
    )
  }

  private getActiveResourceKey(module: string, locale: string): string {
    return `${module}:${locale}`
  }

  private logLoadError(scope: string, error: unknown) {
    const message = error instanceof Error ? error.message : String(error)
    console.warn(`[Lang] Failed to load ${scope}: ${message}`)
  }
}
